// OGC-531 Creatives Manager v19 full-page creative editor: Details, Media &
// copy and Versions tabs beside a live ad preview, with a sticky save bar.
// Save writes a new immutable Draft revision when content changes (kept media
// is copied server-side); details update the creative. Publish is separate
// and confirmed; it never launches Meta ads. Archived creatives are read-only.
// Unsaved edits are protected on every exit.
import { createElement, useEffect, useId, useMemo, useRef, useState } from 'react';
import Beacon from '../../components/Beacon.jsx';
import Menu from '../../components/Menu.jsx';
import Select from '../../components/Select.jsx';
import { notifyCuratedCatalogChanged } from '../../curated-catalog.js';
import { setNavigationGuard } from '../../shell/router.js';
import { AdPreview, CTA_LABELS } from './AdPreview.jsx';
import CatalogDialog from './CatalogDialog.jsx';
import RenovationTypeDialog from './RenovationTypeDialog.jsx';
import { CmIcon } from './creatives-icons.jsx';
import { attemptKey, catalogErrorMessage } from './creatives-api.js';
import { attemptSignature, contentFrom, detailsFrom, mediaFileError, newAttempt, runSave, saveProblem, savePlan } from './creative-save.js';
import useCreativeLifecycle, { campaignUseCount } from './useCreativeLifecycle.jsx';

const TABS = [['details', 'Details'], ['media', 'Media & copy'], ['versions', 'Versions']];
const CTA_OPTIONS = Object.entries(CTA_LABELS).map(([value, label]) => ({ value, label }));

function formatDate(value) {
  return value ? new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
}

function formatBytes(bytes) {
  const value = Number(bytes) || 0;
  if (!value) return '';
  return value >= 1024 * 1024 ? `${(value / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(value / 1024))} KB`;
}

function formatDuration(seconds) {
  const value = Number(seconds);
  if (!(value > 0)) return '';
  const whole = Math.round(value);
  return `${String(Math.floor(whole / 60)).padStart(2, '0')}:${String(whole % 60).padStart(2, '0')}`;
}

// A styled disclosure (the summary marker is drawn in CSS).
function Disclosure({ className, summary, style, children }) {
  return createElement('details', { className, style }, createElement('summary', null, summary), children);
}

// The creative's revisions: the Published one, a newer saved draft (pending)
// and the revision an edit starts from.
export function creativeRevisions(entry) {
  const versions = [...(Array.isArray(entry?.versions) ? entry.versions : [])].sort((a, b) => Number(b.versionNumber) - Number(a.versionNumber));
  const pkg = entry?.package || null;
  const published = versions.find((version) => version.id === pkg?.publishedVersionId) || null;
  const latest = versions[0] || null;
  // Only a Published creative has "draft changes"; an archived one keeps its
  // published pointer, but its latest version is simply what unarchive restores.
  const pending = pkg?.lifecycle === 'published' && published && latest && Number(latest.versionNumber) > Number(published.versionNumber) ? latest : null;
  const current = pkg?.lifecycle === 'published' ? published : latest;
  return { versions, published, pending, current, base: pending || current || latest };
}

export function initialValues(entry) {
  const pkg = entry?.package;
  const { base } = creativeRevisions(entry);
  const details = detailsFrom(pkg || {});
  const content = contentFrom(base || {});
  return {
    displayName: details.displayName,
    internalLabel: pkg ? details.internalLabel : '',
    description: details.description,
    displayOrder: pkg ? details.displayOrder : 1,
    renovationTypeId: pkg?.renovationTypeId || '',
    ...content,
    videoFile: null,
    posterFile: null,
  };
}

function Field({ id, title, help, children, action = null }) {
  return (
    <div className="field">
      <div className="field-title"><label htmlFor={id}>{title}</label>{action}</div>
      {children}
      {help ? <small id={`${id}-help`}>{help}</small> : null}
    </div>
  );
}

export default function CreativeEditor({ apiClient, packageId, onBack, onToast, onCreated, onManageTypes, onRefreshCatalog }) {
  const isNew = !packageId;
  const ids = useId().replaceAll(':', '');
  const [state, setState] = useState({ status: 'loading', entry: null, usage: null, types: [], error: null });
  const [values, setValues] = useState(() => initialValues(null));
  const [tab, setTab] = useState('details');
  const [save, setSave] = useState({ status: 'idle', step: '', error: '' });
  const [media, setMedia] = useState({ videoUrl: '', posterUrl: '', videoInfo: null });
  const [dialog, setDialog] = useState(null);
  const [fieldError, setFieldError] = useState(null);
  const attempt = useRef(null);
  const headingRef = useRef(null);
  const tabRefs = useRef({});
  const loadSequence = useRef(0);
  const mounted = useRef(true);
  const busyRef = useRef(false);
  const modalRef = useRef(false);
  const createdPackage = useRef(null);
  const lifecycleChanged = useRef(false);
  const saveButtonRef = useRef(null);
  const [fileUrls, setFileUrls] = useState({ video: '', poster: '' });

  const entry = state.entry;
  const pkg = entry?.package || null;
  const revisions = useMemo(() => creativeRevisions(entry), [entry]);
  const archived = pkg?.lifecycle === 'archived';
  const saved = useMemo(() => {
    const next = initialValues(entry);
    if (isNew) next.renovationTypeId = state.types.find((type) => !type.archivedAt)?.id || '';
    return next;
  }, [entry, isNew, state.types]);
  const dirty = state.status === 'ready' && !archived && attemptSignature(values) !== attemptSignature(saved);
  const busy = save.status === 'saving';
  const locked = archived || busy;
  busyRef.current = busy;

  // Loads the creative; `keepValues` keeps the person's edits (used when a
  // save already finished on the server but its result was not received).
  async function load({ keepValues = false } = {}) {
    const sequence = ++loadSequence.current;
    try {
      const [types, loadedEntry, usage] = await Promise.all([
        apiClient.typeUsage(),
        isNew ? null : apiClient.entry(packageId),
        isNew ? null : apiClient.packageUsage(packageId).catch(() => null),
      ]);
      if (sequence !== loadSequence.current) return null;
      if (!isNew && !loadedEntry) throw Object.assign(new Error('missing'), { code: 'catalog_package_not_found' });
      setState({ status: 'ready', entry: loadedEntry, usage, types, error: null });
      if (keepValues) return loadedEntry;
      const next = initialValues(loadedEntry);
      if (isNew) next.renovationTypeId = types.find((type) => !type.archivedAt)?.id || '';
      setValues(next);
      return loadedEntry;
    } catch (error) {
      if (sequence === loadSequence.current) setState((current) => ({ ...current, status: 'error', error }));
      return null;
    }
  }

  useEffect(() => {
    mounted.current = true;
    load();
    headingRef.current?.focus({ preventScroll: true });
    window.scrollTo?.(0, 0);
    return () => {
      mounted.current = false;
      loadSequence.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageId]);

  // Signed previews of the revision being edited (or shown, when archived).
  useEffect(() => {
    const base = revisions.base;
    if (!base) { setMedia({ videoUrl: '', posterUrl: '', videoInfo: null }); return undefined; }
    let live = true;
    Promise.all([
      base.videoAssetId ? apiClient.signedUrl(base.id, 'video').catch(() => null) : null,
      base.posterAssetId ? apiClient.signedUrl(base.id, 'poster').catch(() => null) : null,
    ]).then(([video, poster]) => {
      if (!live) return;
      const safe = (descriptor) => (/^https?:\/\//.test(descriptor?.url || '') ? descriptor.url : '');
      setMedia({ videoUrl: safe(video), posterUrl: safe(poster), videoInfo: video });
    });
    return () => { live = false; };
  }, [apiClient, revisions.base?.id]);

  // Protect unsaved edits (and a save in progress) from in-app navigation,
  // browser Back/Forward and leaving the page.
  useEffect(() => {
    if (!dirty && !busy) return undefined;
    setNavigationGuard((target, proceed) => {
      if (busyRef.current) { onToast('Wait for the save to finish before leaving.'); return false; }
      // A dialog is already asking something; it must be closed first.
      if (modalRef.current) return false;
      setDialog({ kind: 'discard', onDiscard: proceed });
      return false;
    });
    const beforeUnload = (event) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', beforeUnload);
    return () => {
      setNavigationGuard(null);
      window.removeEventListener('beforeunload', beforeUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, busy]);

  // Local previews of picked files; each URL is released when replaced.
  useEffect(() => {
    const url = values.videoFile ? URL.createObjectURL(values.videoFile) : '';
    setFileUrls((current) => ({ ...current, video: url }));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [values.videoFile]);
  useEffect(() => {
    const url = values.posterFile ? URL.createObjectURL(values.posterFile) : '';
    setFileUrls((current) => ({ ...current, poster: url }));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [values.posterFile]);
  const previewVideo = values.videoFile ? fileUrls.video : media.videoUrl;
  const previewPoster = values.posterFile ? fileUrls.poster : (values.videoFile ? '' : media.posterUrl);

  function setValue(name, value) {
    setValues((current) => ({ ...current, [name]: value }));
    if (fieldError?.field === name) setFieldError(null);
    if (save.status === 'error') setSave({ status: 'idle', step: '', error: '' });
  }

  function chooseFile(kind, file) {
    if (!file) return;
    const error = mediaFileError(file, kind);
    if (error) { onToast(error); return; }
    setValue(kind === 'video' ? 'videoFile' : 'posterFile', file);
    if (fieldError?.field === kind) setFieldError(null);
    onToast(kind === 'video' ? 'Video selected. Save your draft to keep it.' : 'Poster selected. Save your draft to keep it.');
  }

  function leave(destination) {
    if (busy) { onToast('Wait for the save to finish before leaving.'); return; }
    if (!dirty) { destination(); return; }
    setDialog({ kind: 'discard', onDiscard: destination });
  }

  function selectTab(next, focus = false) {
    setTab(next);
    if (focus) requestAnimationFrame(() => tabRefs.current[next]?.focus());
  }

  function onTabKey(event) {
    const keys = TABS.map(([key]) => key);
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const index = keys.indexOf(tab);
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? keys.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : keys.length - 1)) % keys.length;
    selectTab(keys[next], true);
  }

  // Focus an invalid field, opening a collapsed section and reaching the
  // file input inside a media button.
  function focusField(field) {
    requestAnimationFrame(() => {
      let element = document.getElementById(`${ids}-${field}`);
      if (!element) return;
      const section = element.closest('details');
      if (section) section.open = true;
      if (element.tagName === 'LABEL') element = element.querySelector('input') || element;
      element.focus();
    });
  }

  function restoreSaveFocus() {
    if (!document.activeElement || document.activeElement === document.body) saveButtonRef.current?.focus();
  }

  async function submitSave() {
    if (busy || archived) return false;
    const problem = saveProblem({ values, base: revisions.base, types: state.types });
    if (problem) {
      setTab(problem.tab);
      setFieldError(problem);
      onToast(problem.message);
      focusField(problem.field);
      return false;
    }
    // A new creative whose record was created by a failed attempt is saved
    // as that record from now on, so a retry never creates a second one.
    const target = pkg || createdPackage.current;
    const plan = savePlan({ values, pkg: target, base: revisions.base });
    if (!isNew && !plan.detailsChanged && !plan.typeChanged && !plan.contentChanged) {
      onToast('No new changes to save.');
      return true;
    }
    if (!attempt.current || attempt.current.signature !== attemptSignature(values)) {
      attempt.current = newAttempt({ values, packageId: target?.id || null, previous: attempt.current });
    }
    const submitted = values;
    setSave({ status: 'saving', step: 'Saving…', error: '' });
    try {
      const result = await runSave({
        apiClient,
        attempt: attempt.current,
        values,
        pkg: target,
        base: revisions.base,
        onStep: (step) => { if (mounted.current) setSave((current) => ({ ...current, step })); },
      });
      const savedPackageId = attempt.current.packageId;
      attempt.current = null;
      notifyCuratedCatalogChanged();
      onRefreshCatalog?.();
      if (!mounted.current) return true;
      if (isNew) {
        setSave({ status: 'idle', step: '', error: '' });
        setNavigationGuard(null);
        onToast('Creative saved as a draft.');
        onCreated?.(savedPackageId);
        return true;
      }
      // Fields stay locked until the reload replaces them with the saved copy.
      const reloaded = await load();
      if (!mounted.current) return true;
      setSave({ status: 'idle', step: '', error: '' });
      requestAnimationFrame(restoreSaveFocus);
      const reloadedRevisions = creativeRevisions(reloaded);
      if (!result.versionCreated) onToast('Creative details saved.');
      else if (pkg?.lifecycle === 'published') onToast(`Draft v${reloadedRevisions.pending?.versionNumber ?? result.version?.versionNumber ?? ''} saved. Published v${reloadedRevisions.published?.versionNumber ?? ''} is unchanged.`);
      else onToast('Creative saved as a draft.');
      return true;
    } catch (error) {
      if (isNew && !pkg && attempt.current?.done.package && !createdPackage.current) {
        const { details } = savePlan({ values: submitted, pkg: null });
        createdPackage.current = { id: attempt.current.packageId, ...details, renovationTypeId: submitted.renovationTypeId, lifecycle: 'draft' };
      }
      if (!mounted.current) return false;
      requestAnimationFrame(restoreSaveFocus);
      if (error?.code === 'catalog_version_idempotency_conflict') {
        // An earlier save of this revision finished but its result was lost:
        // load it as the saved state, keep these edits, and start fresh.
        attempt.current = null;
        await load({ keepValues: true });
        if (!mounted.current) return false;
      }
      const message = error?.code === 'catalog_package_in_use'
        ? 'This creative is used by campaigns, so its renovation type can’t change. Archive it and add a new creative under the other type.'
        : catalogErrorMessage(error, 'The draft could not be saved. Your edits are still here; try again.');
      setSave({ status: 'error', step: '', error: message });
      return false;
    }
  }

  function requestPublish() {
    if (busy) return;
    if (dirty) { onToast('Save your draft changes before publishing.'); return; }
    const target = revisions.pending || (pkg?.lifecycle === 'draft' ? revisions.current : null);
    if (!target) return;
    const reasons = (target.blockedReasons || []).map((reason) => reason?.message).filter(Boolean);
    if (target.availability === 'blocked' && reasons.length) {
      setDialog({ kind: 'not-ready', reasons });
      return;
    }
    setDialog({ kind: 'publish', version: target, key: attemptKey('publish', target.id) });
  }

  async function confirmPublish() {
    const { version, key } = dialog;
    setDialog((current) => ({ ...current, busy: true, error: '' }));
    try {
      await apiClient.publishPackage(pkg.id, version.id, key);
      notifyCuratedCatalogChanged();
      onRefreshCatalog?.();
      setDialog(null);
      await load();
      onToast('Creative published. Available for new campaign selections.');
    } catch (error) {
      setDialog((current) => (current ? { ...current, busy: false, error: catalogErrorMessage(error, 'The creative could not be published. Try again.') } : current));
    }
  }

  const row = pkg ? {
    id: pkg.id,
    name: pkg.displayName,
    status: pkg.lifecycle,
    renovation_type_id: pkg.renovationTypeId,
    renovation_type: entry?.renovationType?.displayName,
    pending_version_number: revisions.pending?.versionNumber || null,
    version_id: revisions.current?.id || null,
    version_number: revisions.current?.versionNumber || null,
  } : null;
  const lifecycle = useCreativeLifecycle({
    apiClient,
    types: state.types,
    onChanged: () => { lifecycleChanged.current = true; onRefreshCatalog?.(); },
    onToast,
    onManageTypes,
    onNotice: () => {},
  });
  const usedBy = state.usage ? campaignUseCount(state.usage.references || {}) : 0;

  function actionsMenu() {
    if (!row) return [];
    return [
      archived
        ? { id: 'unarchive', label: 'Unarchive creative', icon: <CmIcon name="unarchive" size={18} />, onSelect: () => lifecycle.unarchive(row) }
        : { id: 'archive', label: 'Archive creative', icon: <CmIcon name="archive" size={18} />, onSelect: () => (dirty
          ? setDialog({ kind: 'discard', onDiscard: () => lifecycle.archive(row) })
          : lifecycle.archive(row)) },
      { id: 'delete', label: 'Delete creative', icon: <CmIcon name="trash" size={18} />, tone: 'danger', separatorBefore: true, onSelect: () => lifecycle.requestDelete(row) },
    ];
  }

  // After a lifecycle change from the editor: archive or delete returns to
  // the catalog, anything else reloads. Cancelled dialogs change nothing.
  const lifecycleOpen = lifecycle.open;
  const wasOpen = useRef(false);
  modalRef.current = Boolean(dialog) || lifecycleOpen;
  useEffect(() => {
    if (wasOpen.current && !lifecycleOpen && pkg && lifecycleChanged.current) {
      lifecycleChanged.current = false;
      apiClient.entry(pkg.id).then((latest) => {
        if (!mounted.current) return;
        if (!latest || (latest.package?.lifecycle === 'archived' && !archived)) {
          setNavigationGuard(null);
          onBack();
        } else load();
      }).catch(() => {});
    }
    wasOpen.current = lifecycleOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lifecycleOpen]);

  const types = state.types.filter((type) => !type.archivedAt || type.id === values.renovationTypeId);
  const fieldId = (name) => `${ids}-${name}`;
  const invalid = (name) => (fieldError?.field === name ? 'true' : undefined);

  let body = null;
  if (tab === 'details') {
    body = (
      <>
        <section className="editor-section">
          <h2>Creative details</h2>
          <Field id={fieldId('displayName')} title="Creative name" help="Shown in the creative catalog and to customers.">
            <input id={fieldId('displayName')} value={values.displayName} maxLength={160} disabled={locked} aria-invalid={invalid('displayName')} aria-describedby={`${fieldId('displayName')}-help`} onChange={(event) => setValue('displayName', event.target.value)} />
          </Field>
          <Field
            id={fieldId('renovationTypeId')}
            title="Renovation type"
            action={archived ? null : <button type="button" className="text-action" disabled={busy} onClick={() => setDialog({ kind: 'add-type' })}>+ Add type</button>}
          >
            <Select
              id={fieldId('renovationTypeId')}
              className="bl-select-block"
              value={values.renovationTypeId}
              onChange={(value) => setValue('renovationTypeId', value)}
              disabled={locked}
              aria-invalid={invalid('renovationTypeId')}
              placeholder="Choose a renovation type"
              options={types.map((type) => ({ value: type.id, label: `${type.displayName}${type.archivedAt ? ' (archived)' : ''}` }))}
            />
          </Field>
          <Field id={fieldId('description')} title={<>Description <span className="required">Optional</span></>} help="A short description for the creative library.">
            <textarea id={fieldId('description')} rows={3} maxLength={1000} value={values.description} disabled={locked} aria-describedby={`${fieldId('description')}-help`} onChange={(event) => setValue('description', event.target.value)} />
          </Field>
          <Disclosure className="secondary-settings" summary="Organization">
            <Field id={fieldId('internalLabel')} title="Internal label" help="For your team. Customers see the creative name.">
              <input id={fieldId('internalLabel')} value={values.internalLabel} maxLength={160} disabled={locked} aria-describedby={`${fieldId('internalLabel')}-help`} onChange={(event) => setValue('internalLabel', event.target.value)} />
            </Field>
            <Field id={fieldId('displayOrder')} title="Display order" help="Lower numbers appear first within this renovation type.">
              <input id={fieldId('displayOrder')} type="number" min="0" max="9999" step="1" value={values.displayOrder} disabled={locked} aria-invalid={invalid('displayOrder')} aria-describedby={`${fieldId('displayOrder')}-help`} onChange={(event) => setValue('displayOrder', event.target.value === '' ? '' : Number(event.target.value))} />
            </Field>
          </Disclosure>
        </section>
        <section className="editor-section">
          <h2>Publishing</h2>
          <p>{archived
            ? 'This creative is archived and is no longer offered for new campaign selections.'
            : pkg?.lifecycle === 'published'
              ? `Version ${revisions.published?.versionNumber} is available for new campaigns. Save your edits as a draft, then publish when ready.`
              : 'Save a draft to review your media and copy before publishing.'}</p>
          <div className="notice">{archived
            ? 'Existing campaigns keep their original creative. Unarchive to review this creative as a draft.'
            : 'Saving does not publish or change existing campaigns.'}</div>
        </section>
      </>
    );
  } else if (tab === 'media') {
    const base = revisions.base;
    const videoName = values.videoFile?.name || (base?.videoAssetId ? 'Saved video' : '');
    const videoMeta = values.videoFile
      ? [formatBytes(values.videoFile.size), 'MP4', 'New upload'].filter(Boolean).join(' · ')
      : [formatDuration(base?.videoAsset?.metadata?.durationSeconds), formatBytes(base?.videoAsset?.sizeBytes || media.videoInfo?.sizeBytes), 'MP4'].filter(Boolean).join(' · ');
    const metadata = values.videoFile ? {} : (base?.videoAsset?.metadata || {});
    body = (
      <>
        <section className="editor-section">
          <h2>Video &amp; poster</h2>
          {videoName ? (
            <div className="media-file">
              {previewPoster ? <img src={previewPoster} alt="Current poster" /> : null}
              <div><strong>{videoName}</strong><small>{videoMeta}</small></div>
            </div>
          ) : <p>Upload a video to complete this creative.</p>}
          {!archived ? (
            <div className="media-actions">
              <label className="file-button" id={fieldId('video')} tabIndex={-1}>
                {videoName ? 'Replace video' : 'Upload video'}
                <input type="file" accept="video/mp4,.mp4" disabled={busy} onChange={(event) => { chooseFile('video', event.target.files?.[0]); event.target.value = ''; }} />
              </label>
              <label className="file-button" id={fieldId('poster')} tabIndex={-1}>
                {previewPoster || base?.posterAssetId ? 'Replace poster' : 'Upload poster'}
                <input type="file" accept="image/jpeg,image/png,image/webp" disabled={busy} onChange={(event) => { chooseFile('poster', event.target.files?.[0]); event.target.value = ''; }} />
              </label>
            </div>
          ) : null}
          <p className="media-help">Video: MP4, up to 512 MB. Poster: JPG, PNG or WebP, up to 10 MB. Without a poster, one is generated from your video.</p>
          <Disclosure className="secondary-settings" style={{ marginTop: 18 }} summary="Media information">
            <dl className="readonly-details">
              <div><dt>Dimensions</dt><dd>{metadata.width && metadata.height ? `${metadata.width} × ${metadata.height}` : '—'}</dd></div>
              <div><dt>Duration</dt><dd>{formatDuration(metadata.durationSeconds) || '—'}</dd></div>
              <div><dt>Codec</dt><dd>{metadata.codec || metadata.videoCodec ? String(metadata.codec || metadata.videoCodec).toUpperCase() : '—'}</dd></div>
              <div><dt>Validation</dt><dd>{values.videoFile ? 'Checked when you save' : base?.videoAssetId ? 'Checked when uploaded' : 'Video required'}</dd></div>
            </dl>
          </Disclosure>
        </section>
        <section className="editor-section">
          <h2>Ad copy</h2>
          <Field id={fieldId('primaryText')} title="Primary text" help="The text shown above your ad. Up to 5,000 characters.">
            <textarea id={fieldId('primaryText')} rows={7} maxLength={5000} value={values.primaryText} disabled={locked} aria-invalid={invalid('primaryText')} aria-describedby={`${fieldId('primaryText')}-help`} onChange={(event) => setValue('primaryText', event.target.value)} />
          </Field>
          <Field id={fieldId('headline')} title="Headline" help="Up to 255 characters.">
            <input id={fieldId('headline')} value={values.headline} maxLength={255} disabled={locked} aria-invalid={invalid('headline')} aria-describedby={`${fieldId('headline')}-help`} onChange={(event) => setValue('headline', event.target.value)} />
          </Field>
          <Field id={fieldId('callToAction')} title="Call to action">
            <Select id={fieldId('callToAction')} className="bl-select-block" value={values.callToAction} onChange={(value) => setValue('callToAction', value)} disabled={locked} options={CTA_OPTIONS} />
          </Field>
        </section>
      </>
    );
  } else {
    const shown = new Set([revisions.pending?.id, revisions.current?.id].filter(Boolean));
    const history = revisions.versions.filter((version) => !shown.has(version.id));
    body = (
      <>
        <section className="editor-section">
          <h2>Version history</h2>
          <p className="tab-description">Each saved version keeps its original media and ad copy.</p>
          {pkg && revisions.versions.length ? (
            <>
              {revisions.pending ? (
                <div className="history-row">
                  <div><strong>v{revisions.pending.versionNumber} · Draft changes</strong><small>Saved draft · Not yet published · {formatDate(revisions.pending.createdAt)}</small></div>
                  <span className="management-status" data-status="draft">Draft</span>
                </div>
              ) : null}
              {revisions.current ? (
                <div className="history-row">
                  <div>
                    <strong>v{revisions.current.versionNumber} · {pkg.lifecycle === 'published' ? 'Published version' : archived ? 'Archived version' : 'Current draft'}</strong>
                    <small>{formatDate(revisions.current.publishedAt || revisions.current.createdAt)}</small>
                  </div>
                  <span className="management-status" data-status={pkg.lifecycle}>{pkg.lifecycle[0].toUpperCase() + pkg.lifecycle.slice(1)}</span>
                </div>
              ) : null}
              {history.map((version) => (
                <div className="history-row" key={version.id}>
                  <div><strong>v{version.versionNumber} · {version.headline || 'Saved version'}</strong><small>{version.publishedAt ? 'Previously published' : 'Previous saved version'} · {formatDate(version.createdAt)}</small></div>
                  <span className="subtle">Read-only</span>
                </div>
              ))}
              {!history.length && Number(revisions.current?.versionNumber) > 1 ? <p className="subtle" style={{ marginTop: 16 }}>Earlier versions are retained in the creative history.</p> : null}
            </>
          ) : <p>Save this creative to create its first version.</p>}
        </section>
        <section className="editor-section">
          <h2>Creative information</h2>
          <dl className="readonly-details">
            <div><dt>Creative key</dt><dd className="metadata-text">{pkg?.packageKey || 'Assigned when saved'}</dd></div>
            <div><dt>Campaign usage</dt><dd>{usedBy ? `${usedBy} campaign${usedBy === 1 ? '' : 's'}` : 'No campaigns'}</dd></div>
            <div><dt>Created</dt><dd>{formatDate(pkg?.createdAt)}</dd></div>
            <div><dt>Media format</dt><dd>{revisions.base?.videoAssetId || values.videoFile ? 'MP4 · H.264' : 'No video yet'}</dd></div>
          </dl>
        </section>
      </>
    );
  }

  const status = pkg?.lifecycle || 'draft';
  const canPublish = !archived && pkg && (pkg.lifecycle === 'draft' ? Boolean(revisions.current) : Boolean(revisions.pending));
  const saveState = archived ? 'Archived · Read-only'
    : save.status === 'saving' ? save.step
      : dirty ? 'Unsaved changes'
        : revisions.pending ? 'Draft changes saved'
          : pkg ? 'No unsaved changes' : 'New creative · Not saved';

  if (state.status === 'error') {
    return (
      <>
        <button type="button" className="back-link" onClick={() => onBack()}><CmIcon name="back" /> Back to creative catalog</button>
        <div className="management-error" role="alert">
          {catalogErrorMessage(state.error, 'This creative could not be loaded.')} <button type="button" onClick={() => load()}>Retry</button>
        </div>
      </>
    );
  }

  return (
    <>
      <button type="button" className="back-link" aria-disabled={busy ? 'true' : undefined} onClick={() => leave(() => onBack())}><CmIcon name="back" /> Back to creative catalog</button>
      <header className="management-page-heading editor-heading">
        <div>
          <span className="management-eyebrow">{pkg ? 'Creative editor' : 'New creative'}</span>
          <div className="editor-title-line">
            <h1 ref={headingRef} tabIndex={-1}>{pkg ? pkg.displayName : 'Add creative'}</h1>
            <span className="management-status" data-status={status}>{status[0].toUpperCase() + status.slice(1)}</span>
          </div>
          <p>{archived
            ? 'View its history, or unarchive it to review as a draft.'
            : revisions.pending ? `Draft v${revisions.pending.versionNumber} · Published v${revisions.published?.versionNumber} stays unchanged`
              : 'Manage the details, media and copy in one place.'}</p>
        </div>
        {pkg ? (
          <div className="header-actions">
            <Menu
              label={`Actions for ${pkg.displayName}`}
              triggerText={<><CmIcon name="more" /> &nbsp; Actions</>}
              triggerClassName="editor-actions-trigger"
              items={actionsMenu()}
              disabled={busy}
            />
          </div>
        ) : null}
      </header>

      {state.status !== 'ready' ? <div className="management-empty" role="status"><Beacon size="inline" delayMs={0} slowAfterMs={null} /> Loading creative…</div> : (
        <div className="editor-grid">
          <div className="editor-main">
            <div className="editor-tabs" role="tablist" aria-label="Creative attributes">
              {TABS.map(([key, label]) => (
                <button
                  key={key}
                  ref={(element) => { tabRefs.current[key] = element; }}
                  type="button"
                  id={`${ids}-tab-${key}`}
                  role="tab"
                  aria-selected={tab === key ? 'true' : 'false'}
                  aria-controls={`${ids}-panel`}
                  tabIndex={tab === key ? 0 : -1}
                  onClick={() => selectTab(key)}
                  onKeyDown={onTabKey}
                >{label}</button>
              ))}
            </div>
            <div id={`${ids}-panel`} role="tabpanel" aria-labelledby={`${ids}-tab-${tab}`}>{body}</div>
          </div>
          <aside className="preview-panel" aria-label="Ad preview">
            <div className="preview-label"><strong>Ad preview</strong><span>Facebook feed</span></div>
            <AdPreview
              primaryText={values.primaryText}
              headline={values.headline}
              callToAction={values.callToAction}
              videoUrl={previewVideo}
              posterUrl={previewPoster}
              mediaLabel={`${state.types.find((type) => type.id === values.renovationTypeId)?.displayName || 'Renovation'} creative`}
              mediaWidth={values.videoFile ? 0 : revisions.base?.videoAsset?.metadata?.width}
              mediaHeight={values.videoFile ? 0 : revisions.base?.videoAsset?.metadata?.height}
            />
            <p className="preview-help">Preview uses a sample business identity. Each campaign uses the customer’s connected Page.</p>
            {usedBy ? <div className="notice">Used by {usedBy} campaign{usedBy === 1 ? '' : 's'}. Their saved media and copy stay unchanged.</div> : null}
          </aside>
        </div>
      )}

      {save.status === 'error' ? <div className="notice page-notice" role="alert"><span>{save.error}</span><button type="button" onClick={submitSave}>Retry save</button></div> : null}

      <div className="save-bar">
        <span className="save-state">{saveState}</span>
        <div>
          <button type="button" onClick={() => leave(() => onBack())} disabled={busy}>{archived ? 'Back to catalog' : 'Cancel'}</button>
          {!archived ? (
            <button ref={saveButtonRef} type="button" className="management-primary" onClick={submitSave} aria-disabled={busy ? 'true' : undefined} disabled={state.status !== 'ready'}>
              {save.status === 'saving' ? 'Saving…' : `Save draft${pkg?.lifecycle === 'published' ? ' changes' : ''}`}
            </button>
          ) : null}
          {canPublish ? <button type="button" onClick={requestPublish} disabled={busy}>Publish…</button> : null}
        </div>
      </div>

      {dialog?.kind === 'discard' ? (
        <CatalogDialog
          open
          title="Discard your changes?"
          description="Your unsaved edits will be lost. The saved creative will stay unchanged."
          onClose={() => setDialog(null)}
          actions={[
            { label: 'Keep editing', autoFocus: true, onClick: () => setDialog(null) },
            { label: 'Discard changes', tone: 'danger', onClick: () => { const next = dialog.onDiscard; setNavigationGuard(null); setValues(saved); setFieldError(null); setDialog(null); if (next) setTimeout(next, 0); } },
          ]}
        />
      ) : null}
      {dialog?.kind === 'not-ready' ? (
        <CatalogDialog open title="Not ready to publish" description="Complete these items before publishing." onClose={() => setDialog(null)}
          actions={[{ label: 'Back to editor', autoFocus: true, onClick: () => setDialog(null) }]}
        >
          <ul>{dialog.reasons.map((reason) => <li key={reason}>{reason}</li>)}</ul>
        </CatalogDialog>
      ) : null}
      {dialog?.kind === 'publish' ? (
        <CatalogDialog
          open
          busy={dialog.busy}
          error={dialog.error || ''}
          title="Publish this creative?"
          description="Make this version available for new campaign selections."
          onClose={() => setDialog(null)}
          actions={[
            { label: 'Keep as draft', autoFocus: true, onClick: () => setDialog(null) },
            { label: 'Publish creative', tone: 'confirm', busyLabel: 'Publishing…', onClick: confirmPublish },
          ]}
        >
          <div className="dialog-description-summary">{pkg.displayName}<small>Version {dialog.version.versionNumber} · {entry?.renovationType?.displayName}</small></div>
          <div className="notice">Existing campaigns keep their saved version. Publishing here does not launch ads in Meta.</div>
        </CatalogDialog>
      ) : null}
      {dialog?.kind === 'add-type' ? (
        <RenovationTypeDialog
          open
          types={state.types}
          apiClient={apiClient}
          onClose={() => setDialog(null)}
          onSaved={async (savedType) => {
            setDialog(null);
            const types = await apiClient.typeUsage().catch(() => null);
            setState((current) => ({ ...current, types: types || [...current.types.filter((type) => type.id !== savedType.id), savedType] }));
            setValue('renovationTypeId', savedType.id);
            onToast('Renovation type added.');
            focusField('renovationTypeId');
          }}
        />
      ) : null}
      {lifecycle.dialog}
    </>
  );
}
